import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Avatar} from 'antd';
import {
  StyledMainNavigation,
  StyledMainNavigationMenuIcon,
} from './StyledMainNavigation';
import Navigation from '../Navigation';
import {
  BookIcon,
  CoinsIcon,
  CreditCardRoundedIcon,
  ExpensesIcon,
  FolderFilledIcon,
  HandShakeIcon,
  HomeIcon,
  InvoiceIcon,
  NetSuiteIcon,
  TransactionOrderIcon
} from '../../icons';
import routesList from '../../routes/routes.json';
import {cardsHelpers, subscriptionsHelpers, textHelpers} from '../../utils/helpers';
import {
  availableModulesConstants,
  subscriptionPaymentTypesConstants,
  subscriptionViewTypesConstants
} from '../../constants';
import {cardActions} from '../../state/actions/card.actions';
import {mainActions} from '../../state/actions/main.actions';
import {subscriptionActions} from '../../state/actions/subscription.actions';
import {
  useHasObAccounts,
  useIsAccountingAvailable,
  useIsAccountingExportMode,
  useIsAccountingModeResolved,
  useIsAdmin,
  useIsBankingEnabled,
  useIsBudgetsAvailable,
  useIsExpensesDisabled,
  useIsManager,
  useIsPsd2ObAvailable,
  useIsEmployeeChanged,
  useIsEmployeeLoaded,
  useIsRequiredCardVerification,
  useIsSubscriptionsDisabled,
  useIsTreezorEnabled,
  useIsVendorsDisabled
} from '../../hooks';

const {CARDS, TRANSACTIONS, SUBSCRIPTIONS} = availableModulesConstants;

const MainNavigation = ({...rest}) => {
  const [t] = useTranslation('main');
  const dispatch = useDispatch();

  const availableModules = useSelector((state) => state.main.availableModules?.modules || []);
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isExistsBankingUser = useSelector((state) => state.banking?.verification?.isCreatedUser);
  const hasObAccounts = useHasObAccounts();

  const [userCards, setUserCards] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [navigationProps, setNavigationProps] = useState({items: []});

  const isEmployeeLoaded = useIsEmployeeLoaded();

  const isEmployeeChanged = useIsEmployeeChanged();

  const isAccountingAvailable = useIsAccountingAvailable();
  const isAccountingExportMode = useIsAccountingExportMode();
  const isAccountingModeResolved = useIsAccountingModeResolved();
  const isBudgetsAvailable = useIsBudgetsAvailable();
  const isPsd2ObAvailable = useIsPsd2ObAvailable();

  const isExpensesDisabled = useIsExpensesDisabled();
  const isSubscriptionsDisabled = useIsSubscriptionsDisabled();
  const isVendorsDisabled = useIsVendorsDisabled();

  const isTreezorEnabled = useIsTreezorEnabled();
  const isBankingEnabled = useIsBankingEnabled();

  const {
    checked: isCheckedBankingDetails,
    required: isRequiredCardVerification
  } = useIsRequiredCardVerification();

  const isDisabledModule = (name) => !availableModules.includes(name);

  const finishLoading = () => setLoading(false);

  const storeNavigationProps = (items = []) => {
    setNavigationProps({items});
    dispatch(mainActions.setNavigationMenuProps({items: items.map(({ icon, ...rest }) => rest)}));
  }

  const formatNavigationItem = (item) => ({
    className: item.className || undefined,
    children: item.children ? item.children.map(i => formatNavigationItem(i)) : undefined,
    dividedKey: item.dividedKey,
    disabled: item.disabled,
    icon: item.icon,
    key: item.title,
    label: t(`navigation.${item.title}`),
    path: item.path
  });

  const loadCards = () => {
    dispatch(cardActions.getCards(
      undefined,
      (response) => {
        setUserCards(response?.results || []);
        finishLoading();
      },
      finishLoading
    ));
  }

  const loadSubscriptions = () => {
    dispatch(subscriptionActions.getSubscriptionsList(
      {view: subscriptionViewTypesConstants.AUTHORIZED},
      (response) => {
        setUserSubscriptions(response?.subscriptions || []);
        finishLoading();
      },
      finishLoading
    ));
  }

  const navigationItems = useMemo(() => {
    let items = [...navigationProps.items];
    if (!isAdmin && !isManager) {
      items = [
        formatNavigationItem({
          icon: <HomeIcon />,
          path: routesList.overview,
          title: 'overview'
        }),
        isBudgetsAvailable && formatNavigationItem({
          children: [
            { path: routesList.budgetsActive, title: 'budgetsActive' },
            { path: routesList.budgetsRequests, title: 'budgetsRequests' },
          ],
          icon: <CoinsIcon />,
          title: 'budget'
        }),
        !isExpensesDisabled && formatNavigationItem({
          icon: <ExpensesIcon />,
          path: routesList.expensesList,
          title: 'expenses'
        }),
        (isPsd2ObAvailable && hasObAccounts) && formatNavigationItem({
          icon: <TransactionOrderIcon />,
          path: routesList.transactionsList,
          title: 'account'
        }),
        ...items,
      ]
    }
    return items.filter(Boolean);
  }, [isAdmin, isManager, isExistsBankingUser, navigationProps.items, isBudgetsAvailable, isExpensesDisabled, isPsd2ObAvailable, hasObAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isTreezorEnabled) setLoading(true);
  }, [isEmployeeChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmployeeLoaded && !isAdmin && !isManager && isBankingEnabled) {
      setLoading(true);
      loadCards();
      loadSubscriptions();
    } else {
      setLoading(false);
      dispatch(mainActions.setNavigationLoading(false));
    }
  }, [isEmployeeLoaded, isBankingEnabled, isRequiredCardVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin && !isManager) return;

    const disabledTransactions = isBankingEnabled ? isDisabledModule(TRANSACTIONS) : true;

    // Single source of truth for the admin/manager navigation.
    // `available` controls who sees each item: items without it show for both roles,
    // admin-only items gate on `isAdmin`.
    const items = [
      {
        icon: <HomeIcon />,
        path: routesList.overview,
        title: 'overview'
      },
      {
        available: isAdmin && isBudgetsAvailable,
        children: [
          { path: routesList.budgetsActive, title: 'budgetsActive' },
          { path: routesList.budgetsRequests, title: 'budgetsRequests' },
        ],
        icon: <CoinsIcon />,
        title: 'budget'
      },
      {
        available: isAdmin && !isVendorsDisabled,
        icon: <HandShakeIcon />,
        path: routesList.vendorsList,
        title: 'vendors'
      },
      {
        available: !isSubscriptionsDisabled,
        icon: <FolderFilledIcon />,
        path: routesList.subscriptionsList,
        title: 'subscriptions',
      },
      {
        available: !isExpensesDisabled,
        icon: <ExpensesIcon />,
        path: routesList.expensesList,
        title: 'expenses'
      },
      {
        available: isTreezorEnabled,
        disabled: isDisabledModule(CARDS),
        icon: <CreditCardRoundedIcon />,
        path: routesList.cardsList,
        title: 'cards',
      },
      {
        available:
          (isAdmin && (isPsd2ObAvailable || isTreezorEnabled)) ||
          (isManager && isPsd2ObAvailable && hasObAccounts),
        disabled: isPsd2ObAvailable ? false : isTreezorEnabled ? disabledTransactions : false,
        icon: <TransactionOrderIcon />,
        path: routesList.transactionsList,
        title: 'account'
      },
      {
        dividedKey: 'accounting',
        icon: <InvoiceIcon />,
        path: routesList.invoicesList,
        title: 'invoices'
      },
      {
        // Waits for the mode. Rendering before it resolves painted "NetSuite" at a Manual ERP
        // company and then corrected itself, which the pilot client reported as a bug
        // (H1MVP-4791). Hidden for a beat is honest; the wrong ERP's name is not.
        available: isAdmin && isAccountingAvailable && isAccountingModeResolved,
        className: 'netsuite-menu-item',
        children: [
          { path: routesList.netSuiteVendors, title: 'netsuiteVendors' },
          { path: routesList.netSuiteVendorBills, title: 'vendorBills' },
          { path: routesList.netSuiteJournalEntriesList, title: 'journalEntries' }
        ],
        dividedKey: 'accounting',
        // A company running its own ERP is not integrated with NetSuite, so the section is named
        // for what it does rather than for a product they do not have — and the vendor logo comes
        // off with the name.
        icon: isAccountingExportMode ? <BookIcon /> : <NetSuiteIcon />,
        title: isAccountingExportMode ? 'accounting' : 'netsuite'
      }
    ];

    setLoading(false);

    // clear previous state is needed
    userCards.length && setUserCards([]);
    userSubscriptions.length && setUserSubscriptions([]);

    // show only available features
    storeNavigationProps(
      items
        .filter(i => i.available !== false)
        .map(i => formatNavigationItem(i))
    );
    // isAccountingExportMode belongs here and is easy to miss: the accounting settings that carry
    // it arrive after the first render, so without it the menu is built once as NetSuite and never
    // rebuilt — correct only when the fetch happens to beat the render.
  }, [isAdmin, isManager, t, isRequiredCardVerification, availableModules, isAccountingAvailable, isAccountingExportMode, isAccountingModeResolved, isTreezorEnabled, isBudgetsAvailable, isExpensesDisabled, isPsd2ObAvailable, isSubscriptionsDisabled, isVendorsDisabled, hasObAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let items = [];
    if (!isAdmin && !isManager) {
      let id, label, logo, key, ownerId;
      const isDisabledSubscriptionPage = isDisabledModule(SUBSCRIPTIONS) || isRequiredCardVerification;
      const isDisabledCardPage = isDisabledModule(CARDS) || isRequiredCardVerification;
      // create menu array from subscriptions
      // hidden entirely when the module is off for the company
      const subscriptionsItems = (isSubscriptionsDisabled ? [] : userSubscriptions).map(subscription => {
        id = subscription.id;
        key = `subscription-${id}`;
        label = subscriptionsHelpers.getSubscriptionName(subscription);
        logo = subscription?.service?.logo || undefined;
        return {
          dividedKey: 'subscriptions',
          disabled: isDisabledSubscriptionPage && subscription.payment_type === subscriptionPaymentTypesConstants.CARD,
          icon: (
            <Avatar src={logo} size={18}>
              {textHelpers.getInitials(label)}
            </Avatar>
          ),
          key,
          label,
          path: `${routesList.subscriptionsList}/${id}`,
          variant: 'subscription'
        }
      }).sort((a, b) => a.label.localeCompare(b.label));

      // create menu array from cards
      if (isTreezorEnabled && userCards.length) {
        userCards.forEach((card) => {
          id = card.card_id;
          key = `card-${id}`;
          ownerId = card?.owner?.id;
          items.push({
            disabled: isDisabledCardPage,
            dividedKey: 'cards',
            icon: (
              <StyledMainNavigationMenuIcon>
                {cardsHelpers.getCardIcon(card)}
              </StyledMainNavigationMenuIcon>
            ),
            key,
            label: `${card?.masked_pan ? '**' : ''}${cardsHelpers.getLast4Digits(card?.masked_pan)}`,
            path: ownerId && `${routesList.cardsList}/${ownerId}/${id}`,
            variant: 'card'
          });
        });
      }

      // finish loading if banking details is checked
      if (isCheckedBankingDetails) {
        finishLoading();
        dispatch(mainActions.setNavigationLoading(false));
      }

      subscriptionsItems.forEach(item => {
        items.push(item);
      });

      storeNavigationProps(items);
    }
  }, [isCheckedBankingDetails, userCards, userSubscriptions, isSubscriptionsDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledMainNavigation {...rest}>
      <Navigation
        items={navigationItems}
        loading={loading}
      />
    </StyledMainNavigation>
  )
}

export default MainNavigation;
